import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { setPendingDraftReply } from "@/lib/pending-draft-reply";
import { setPendingAutoJoin } from "@/lib/pending-auto-join";
import { trackPublicPageCtaClicked } from "@/lib/analytics-events";

interface PublicReplyComposerProps {
  /** Public-workspace slug — the auto-join + draft-restore target. */
  slug: string;
  /** Channel the reply posts into (from `fetchPublicChannelContent`'s
   *  `channel.id`). A top-level reply targets this channel directly. */
  channelId: string;
  /** Human channel name — restored alongside the draft so the post-auth
   *  hook can navigate back to the right place. */
  channelName: string;
  /**
   * The question/message being answered (`…/m/<id>` anchor link), or null
   * for a plain top-level channel reply. Drives whether the post-auth hook
   * publishes via `addReply` (thread) or `insert` (top-level).
   */
  targetMessageId?: string | null;
  /**
   * Whether this workspace's `public_join_policy` is `auto`. Only then do we arm
   * the post-auth auto-join; a `request`-policy workspace would 409 the auto-join
   * (membership needs admin approval), so we don't pre-arm a doomed one.
   */
  autoJoin: boolean;
  /** Composer placeholder. Defaults to a channel-reply prompt. */
  placeholder?: string;
  /** Start the one-click GitHub sign-up flow (AuthGate's GitHub handler). */
  onContinueWithGithub: () => void;
}

/**
 * Lightweight reply composer for the LOGGED-OUT public-workspace surface.
 *
 * This renders OUTSIDE `<ZeroProvider>` with no auth token, so it is REST-only
 * by construction — it can't post via a mutator. Instead, on Send it stashes
 * the draft (sessionStorage), arms the auto-join intent, and kicks off the
 * GitHub sign-up. After auth, `use-pending-draft-reply` restores the draft and
 * publishes the reply automatically.
 *
 * NOT a Tiptap composer (Tiptap is heavy + assumes the authenticated app
 * shell). A plain design-system `Textarea` + `Button` is the right weight here.
 * The visitor is ALWAYS logged out on this surface, so Send only ever runs the
 * stash-then-signup path — there is no authenticated branch.
 */
export function PublicReplyComposer({
  slug,
  channelId,
  channelName,
  targetMessageId,
  autoJoin,
  placeholder,
  onContinueWithGithub,
}: PublicReplyComposerProps) {
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const trimmed = content.trim();
  const isEmpty = trimmed.length === 0;

  const handleSend = () => {
    if (isEmpty) {
      // Inline (no Toaster — these early-return surfaces don't mount one).
      setError("Write a reply first.");
      return;
    }
    setError(null);
    try {
      // `setPendingDraftReply` stamps `ts` itself (the TTL guard) — callers
      // pass everything but `ts`.
      setPendingDraftReply({
        slug,
        channelId,
        channelName,
        targetMessageId: targetMessageId ?? null,
        content: trimmed,
      });
      // Only arm auto-join for `auto`-policy workspaces; a `request`-policy
      // workspace needs admin approval, so a pre-armed auto-join would 409.
      if (autoJoin) setPendingAutoJoin(slug);
      trackPublicPageCtaClicked({
        slug,
        action: "signup",
        placement: "conversion_block",
      });
      onContinueWithGithub();
    } catch {
      // sessionStorage / handler failure — surface inline, keep the text so
      // the visitor can retry rather than silently losing their reply.
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={content}
        onChange={(e) => {
          setContent(e.target.value);
          if (error) setError(null);
        }}
        placeholder={
          placeholder ?? "Write a reply — sign up to post it instantly…"
        }
        aria-label="Your reply"
        className="min-h-[88px]"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={handleSend} disabled={isEmpty}>
          Continue with GitHub
        </Button>
        <p
          className={cn(
            "text-xs text-muted-foreground",
            isEmpty && "opacity-80",
          )}
        >
          Your reply posts the moment you connect GitHub.
        </p>
      </div>
    </div>
  );
}
